import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { PrerenderService } from '@df/browser/storage/prerender/prerender.service';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { StructuralDataServiceDecorator } from './structural-data.service.decorator';
import { dedupeReviews } from './trustpilot-review.service';

/**
 * Upper bound (ms) for how long the prerender snapshot is held waiting for
 * Trustpilot to inject its schema. The wait is released early as soon as we
 * merge Trustpilot's data; this timeout only guards against Trustpilot never
 * injecting (e.g. non-product pages), so the snapshot is never blocked forever.
 */
const TRUSTPILOT_PRERENDER_TIMEOUT = 4000;

/**
 * The subset of a schema.org Product block that we lift out of Trustpilot's
 * injected JSON-LD before discarding it.
 */
interface ITrustpilotProductReviews {
    review?: StructuralData.IReviewData | StructuralData.IReviewData[];
    aggregateRating?: StructuralData.IAggregateRatingData;
}

/**
 * Watches the document for the JSON-LD `Product` schema that the Trustpilot
 * widget injects client-side, lifts its reviews + aggregateRating into our own
 * Product schema, and removes the duplicate Trustpilot script so Google sees a
 * single, merged Product block.
 *
 * During prerendering Trustpilot runs in the headless browser, so we hold the
 * prerender snapshot open (via PrerenderService) until the merge is done,
 * ensuring the frozen HTML contains the single merged schema rather than the
 * duplicate.
 */
@Injectable({ providedIn: 'root' })
export class TrustpilotSchemaObserverService implements OnDestroy {
    private readonly LD_JSON_SELECTOR = 'script[type="application/ld+json"]';

    private readonly structuralData = inject(StructuralDataServiceDecorator);
    private readonly prerenderService = inject(PrerenderService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject<Document>(DOCUMENT);
    private observer?: MutationObserver;
    private releasePrerender?: () => void;

    init(): void {
        if (!isPlatformBrowser(this.platformId) || this.observer) {
            return;
        }

        // Hold the prerender snapshot until we have merged Trustpilot's schema
        // (or the timeout fires). No-op outside prerender modes.
        this.prerenderService.wait(
            new Promise<void>(resolve => {
                this.releasePrerender = resolve;
            }),
            TRUSTPILOT_PRERENDER_TIMEOUT
        );

        // MutationObserver only reports nodes added AFTER observe(), reports a
        // freshly-inserted node before its JSON content is filled in, and never
        // retries once a node is seen. Trustpilot hits all three cases, so instead
        // of inspecting individual added nodes we re-sweep every foreign ld+json
        // script in the DOM on each mutation batch (and once now), retrying until
        // our product schema is available to merge into.
        this.observer = new MutationObserver(() => this.sweep());
        this.observer.observe(this.document, { childList: true, subtree: true });
        this.sweep();
    }

    private sweep(): void {
        const scripts = Array.from(this.document.querySelectorAll(this.LD_JSON_SELECTOR));

        for (const script of scripts) {
            if (this.structuralData.isManagedLdJsonScript(script)) {
                continue;
            }
            const product = this.findForeignProductNode(script.textContent);
            if (!product) {
                continue;
            }
            const extracted = this.extractProductReviews(product);
            if (extracted && this.structuralData.applyExternalProductReviews(extracted)) {
                // Merged Trustpilot's reviews into our Product block; drop the duplicate.
                script.remove();
                this.releaseOnce();
            } else if (this.structuralData.isCurrentProductNode(product)) {
                // A foreign Product block describing OUR product with nothing left to
                // merge — either Trustpilot holds no reviews for it, or its reviews are
                // already in our schema. Strip it either way so Google sees a single
                // Product entity: Trustpilot's block disagrees with ours on sku and
                // points at a non-canonical variant URL. Scoped by product identity so
                // unrelated third-party Product JSON-LD is never removed; that check
                // also fails while our own schema is still absent, in which case the
                // sweep retries on a later mutation batch.
                script.remove();
                this.releaseOnce();
            }
        }
    }

    private releaseOnce(): void {
        this.releasePrerender?.();
        this.releasePrerender = undefined;
    }

    /**
     * Parses the text content of an ld+json script and returns its Product node,
     * or `undefined` when the text is not a (foreign) product schema.
     */
    private findForeignProductNode(jsonText: string | null | undefined): Record<string, unknown> | undefined {
        if (!jsonText) {
            return undefined;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(jsonText);
        } catch {
            return undefined;
        }

        return this.findProductNode(parsed);
    }

    /**
     * Returns the deduplicated `review` + `aggregateRating` of a Product node, or
     * `undefined` when there is nothing to merge.
     */
    private extractProductReviews(product: Record<string, unknown>): ITrustpilotProductReviews | undefined {
        const review = dedupeReviews(
            product['review'] as StructuralData.IReviewData | StructuralData.IReviewData[] | undefined
        );
        const aggregateRating = product['aggregateRating'] as StructuralData.IAggregateRatingData | undefined;
        if (!review.length && !aggregateRating) {
            return undefined;
        }

        const result: ITrustpilotProductReviews = {};
        if (review.length) {
            result.review = review;
        }
        if (aggregateRating) {
            result.aggregateRating = aggregateRating;
        }
        return result;
    }

    /**
     * Collects every object node reachable from the parsed JSON-LD, unwrapping
     * top-level arrays and `@graph` wrappers, then returns the first Product node.
     */
    private findProductNode(parsed: unknown): Record<string, unknown> | undefined {
        const candidates: Record<string, unknown>[] = [];
        const collect = (value: unknown): void => {
            if (!value) {
                return;
            }
            if (Array.isArray(value)) {
                value.forEach(collect);
                return;
            }
            if (typeof value === 'object') {
                const node = value as Record<string, unknown>;
                candidates.push(node);
                if (node['@graph']) {
                    collect(node['@graph']);
                }
            }
        };
        collect(parsed);
        return candidates.find(node => this.isProductType(node['@type']));
    }

    private isProductType(type: unknown): boolean {
        if (Array.isArray(type)) {
            return type.some(item => this.isProductType(item));
        }
        return type === StructuralData.Type.product || type === StructuralData.Type.productGroup;
    }

    ngOnDestroy(): void {
        this.releasePrerender?.();
        this.releasePrerender = undefined;
        this.observer?.disconnect();
        this.observer = undefined;
    }
}
